import { Stack, Typography, TextField, FormControlLabel, Checkbox, Chip, Grid, Tooltip, Button } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { apiGet } from '../../generic/Api_Functions'
import { durationStringToMilliseconds, GetUtcTime } from '../../generic/DateTimeFunctions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueLabel, systemValueOptionText } from '../../generic/systemValues/systemValueLabel'
import ReturnDateField from './ReturnDateField'
import ReturnDurationField from './ReturnDurationField'
import PeriodicReturnPopoverField from './PeriodicReturnPopoverField'
import { SmartSelect } from '../../generic/smartSelect'
import SmartTextField from '../../generic/smarttextfield/SmartTextField'
import { collectFeatureGroupIndexValues, filterAdmissibleIndexValues } from './indexDegradation'
import { applyJobReferenceCascadeIndexValue, collectJobReferenceCascadeItems } from './cascadeApproval'
import CascadeIndexChangeDialog from './dialogs/CascadeIndexChangeDialog'
import ExternResetDialog from './dialogs/ExternResetDialog'
import { formatCodeName } from '../../generic/utilities/objectStructures/codeNameFormat'
import { isExternalEffective, resolveExternCheckboxGating } from './jobExternalCharacter'
import { markExternalEmptyingPlannerFields, plannerFieldsAlreadyEmpty } from './jobExternalReset'
import { managesPreparation, resolvePreparationCheckboxGating } from './jobPreparationCharacter'
import { isAnotherJobTheNightJob, isNightJobEffective, NIGHT_JOB_CONTEXT_OFF, resolveExternGatingWithNightMark, resolveNightCheckboxGating } from './jobNightCharacter'
import NodeOperationLinkHeader from './NodeOperationLinkHeader'
import PublishOperationDialog from './PublishOperationDialog'
import OperationCustomizationDialog from './OperationCustomizationDialog'
import UnlinkOperationDialog from './UnlinkOperationDialog'
import { resolveOperationLinkState } from './operationLinkIntegrity'
// D12 (`IN16`): el par de origen se lee y se normaliza en un solo sitio del cliente (E6).
import { toSourceProcessPair } from './processOriginHighlight'

/**
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').JobReference} JobReference JobReference object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').ProgressCalculationType} ProgressCalculationType ProgressCalculationType object
 */

/**
 * Historia 152 (S26/S27, CU-07 · `RN-01.13b` punto 3, `RN-G5`): el bloque **Origin** (J39) con el
 * **proceso de origen** (J22) del nodo.
 *
 * Va aparte de la procedencia del vínculo porque son dos cosas distintas: el vínculo puede
 * romperse o desvincularse y el origen sigue ahí, inmutable desde que el nodo se creó (IN8). Lo
 * tienen los JOB vinculados, los jobs locales copiados y **también los nodos WORKFLOW** arrastrados
 * de una plantilla — por eso vive en su propio componente: el panel de un nodo WORKFLOW no tiene
 * propiedades de job que mostrar, pero sí tiene origen (S27).
 *
 * Sin proceso de origen NO se pinta nada: no se inventa (CU-07 A1).
 *
 * *(D12, Enmienda 1)* El origen es el **par** `(código, versión)`: `Source process` (J22) sigue
 * siendo el **RÓTULO** —no cambia de literal— y el **VALOR** es la clave **J53**
 * `{{code}} v{{version}}`, **una sola clave con interpolación** (como `#130 K32`): la "v" y el
 * orden de los dos datos son traducibles y **no se concatenan trozos traducidos**. El par es
 * indivisible (`IN16`): con medio par no se pinta nada, porque "PROC-A v" no es un dato.
 *
 * @param {Object} props
 * @param {string} props.sourceProcessCode
 * @param {number|null} props.sourceProcessVersionNumber La versión de la plantilla — DECIMAL (R17)
 */
export const NodeOriginBlock = ({ sourceProcessCode = '', sourceProcessVersionNumber = null }) => {
  const { t_ } = useSmartTranslation()
  const origin = toSourceProcessPair(sourceProcessCode, sourceProcessVersionNumber)
  if (!origin.sourceProcessCode) {
    return null
  }
  return (
    <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap data-testid='node-origin'>
      <Typography variant='caption'>{t_('Origin')}</Typography>
      <Typography variant='caption' color='text.secondary'>{t_('Source process')}</Typography>
      <Chip
        size='small'
        variant='outlined'
        data-testid='node-source-process'
        label={t_('{{code}} v{{version}}', {
          code: origin.sourceProcessCode,
          version: origin.sourceProcessVersionNumber
        })}
      />
    </Stack>
  )
}

/**
 *
 * @param {Object} props
 * @param {JobReference} props.jobReference - Job reference
 * @param {boolean} props.editing - Is editing enabled
 * @param {function(JobReference)} props.updateJobReference - Function to update the linked job reference
 * @param {ProgressCalculationType[]} props.progressCalculationTypes - Progress calculation types array
 * @param {import('../../generic/utilities/objectStructures/globalStructures').IdName[]} props.indexValues
 * @param {boolean} props.workflowUseIndex
 * @param {boolean} [props.fabricationOrderOperable] Whether the FO admits execution. Defaults to `true`
 *   because template and subworkflow contexts have no FO at all: RN22 fixes that the absence of this
 *   factor must NOT block, following the editor's own prop-chain convention (and deliberately not
 *   `isFabricationOrderOperable(undefined)`, which fails safe to `false` for a different purpose).
 * @param {{enabled: boolean, nightJobNodeIds: string[]}} [props.nightJobContext] Story 183: whether the
 *   night job feature is active (C4) and which job nodes of the workflow carry the mark. Off by default:
 *   without it the "Night job" control does not exist (RN6.3).
 * @param {boolean} [props.featureGroupRevisionControlled] Story 186: CONTROLLED feature group revisions; the Index cascade then applies only legal steps (RN49)
 * @param {Object<string, object>} [props.featureGroupBaselines] Story 186: persisted copy of each feature group by id
 * @returns
 */
const NodeGeneralProperties = ({ jobReference, node, linkedJobReference = null, canPublishOperation = false, workflowId = 0, onOperationPublish = null, operationLink = null, onOperationUpdate = null, onOperationDismiss = null, onOperationUnlink = null, canViewOperationLibrary = false, onOpenOperationDetail = null, sourceProcessCode = '', sourceProcessVersionNumber = null, technologyEditable = false, operationIdentity = {}, onOperationIdentityChange = null, editing, updateJobReference, progressCalculationTypes, indexValues, workflowUseIndex, cascadeApprovalEnabled = false, featureGroupRevisionControlled = false, featureGroupBaselines = {}, fabricationOrderOperable = true, nightJobContext = NIGHT_JOB_CONTEXT_OFF }) => {
  const { t_ } = useSmartTranslation()
  // S49-S56/RN21: the four conditions in conjunction, resolved by the single owner of the predicate so
  // the checkbox can never disagree with the disabled fields or the node colour.
  // Story 183 (RN6.6): the night job cannot be made external — the night block is layered over that
  // gating by the night owner, and it is a no-op with the feature inactive (S88).
  const externGating = resolveExternGatingWithNightMark(
    resolveExternCheckboxGating({ jobReference, editing, fabricationOrderOperable }),
    jobReference,
    nightJobContext
  )
  // Story 113 (S98/S103-S106): its own gating, from its own owner module. NOT `externGating`: D4
  // signed a deliberate divergence -- produced parts do not block this one (see
  // `jobPreparationCharacter.js` for why).
  const preparationGating = resolvePreparationCheckboxGating({ editing, fabricationOrderOperable })
  // Story 183 (RN6, RN7): the night mark's own gating, from its own owner module — same answer the
  // Process Tree job form gets for this node.
  const nightGating = resolveNightCheckboxGating({
    jobReference,
    editing,
    fabricationOrderOperable,
    anotherNightJob: isAnotherJobTheNightJob(nightJobContext, node?.id)
  })
  // HU-03 (S63/RN25.4, widened by DN-2): the fifth field of the story — the job's own `Operators`, the
  // one the planner uses to SIZE the job. A subcontracted JOB is worked by no operator of ours. Fix
  // #2885: the value is emptied when the mark goes on (see `jobExternalReset.js`), not merely disabled.
  const jobOperatorsDisabled = !editing || isExternalEffective(jobReference)
  // Story 146 (S63/RN30, RN31 · CU3/CU6-A6.1): an external JOB is planned on an external asset, so its
  // preferred asset is a value the planner will never read (RN13). Emptying it when the mark goes on
  // (`jobExternalReset.js`) protects the DATA; showing the selector empty and disabled protects the USER
  // from believing he chose something. The displayed value is forced empty instead of trusted, because a
  // node marked external elsewhere may still carry a preferred asset persisted before the mark
  // (CU6/A6.2) — it is not offered back as if it were a choice. The balanced line position is not edited
  // in this panel (it is written by the balanced line editor), so only the warning names it here.
  const preferenceDeviceBlocked = isExternalEffective(jobReference)
  // Fix #2885: ticking Extern on a job that still carries planner-facing values asks before emptying them.
  // S73/S116 (HU-07): el diálogo de guardar este nodo en la biblioteca.
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  // Historia 152 (S38, S47 · CA1.9, CA1.11): los dos diálogos de las acciones del vínculo (E4, E5),
  // montados solo mientras están abiertos, como hace la cabecera con el impacto (C6).
  const [customizationDialogOpen, setCustomizationDialogOpen] = useState(false)
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false)
  const [externResetDialogOpen, setExternResetDialogOpen] = useState(false)
  // Historia 130 (ajuste del PO 2026-09-15): en la vista OPERATIONS no hay grafo, y la tecnología de
  // un nodo del grafo se cambia DESDE el nodo. Una operación de biblioteca necesita cambiarla aquí,
  // tanto al crearla como al editar una versión.
  const [technologies, setTechnologies] = useState([])

  useEffect(() => {
    if (!technologyEditable) return
    let current = true
    apiGet('technologiesWorkflow/noPagination')
      .then((list) => { if (current) setTechnologies(Array.isArray(list) ? list : []) })
      .catch(() => { if (current) setTechnologies([]) })
    return () => { current = false }
  }, [technologyEditable])

  const changeTechnology = (technologyName) => {
    const technology = technologies.find((option) => option.name === technologyName)
    if (!technology) return
    updateJobReference({
      ...jobReference,
      technology,
      // Misma regla que `UPDATE_JOB_TECHNOLOGY` en el grafo: el tipo de cálculo de progreso sigue a
      // la tecnología cuando esta trae uno, y si no se queda como estaba.
      progressCalculationTypeDTO: technology.progressCalculationType ?? jobReference?.progressCalculationTypeDTO
    })
  }
  const changeExternMark = (external) => {
    if (external && !plannerFieldsAlreadyEmpty(jobReference)) {
      setExternResetDialogOpen(true)
      return
    }
    updateJobReference({ ...jobReference, external })
  }
  const [ruleList, setRuleList] = useState([])
  const [deviceList, setDeviceList] = useState([])
  const [fixtureRefList, setfixtureRefList] = useState([])
  const [firstLoad, setFirstLoad] = useState(true)
  // Enmienda PO 23/07/2026 (HU-04/HU-05): with WORKFLOW_CASCADE_APPROVAL active, changing the
  // jobReference index to a value its featureGroups do not match opens a confirmation modal
  // listing them; accepting cascades the selected value to every travelling featureGroup,
  // cancelling keeps everything (including the jobReference) at its previous value.
  const featureGroupCascadePolicy = { controlled: featureGroupRevisionControlled }
  const [cascadeDialog, setCascadeDialog] = useState({ open: false, targetIndexValue: {}, items: [] })
  /** @type {[import('../../generic/utilities/objectStructures/extendedcolumn/extendedColumn').ExtendedColumn[], React.Dispatch<SetStateAction<import('../../generic/utilities/objectStructures/extendedcolumn/extendedColumn').ExtendedColumn[]>>]} */
  const [extendedColumns, setExtendedColumns] = useState([])

  useEffect(() => {
    const load = async () => {
      setRuleList(await apiGet('rules'))
      setfixtureRefList(await apiGet('fixtureReferences/'))
      setExtendedColumns(await apiGet('extendedColumn/job_reference'))
    }
    if (firstLoad) {
      load()
      setFirstLoad(false)
    }
  }, [firstLoad])

  useEffect(() => {
    const loadDevices = async () => {
      setDeviceList(await apiGet('devices/' + jobReference?.technology?.id))
    }
    if (!jobReference) {
      setDeviceList([])
      return
    }
    loadDevices()
  }, [jobReference])

  const jobInfo = useMemo(() => {
    const info = { totalTime: 0, toolQuantity: 0 }
    if (!jobReference) {
      return info
    }
    jobReference.taskReferences.forEach(task => {
      try {
        info.totalTime += durationStringToMilliseconds(task.theoreticalTime, t_)
        info.toolQuantity += task.taskToolInfos?.length
      } catch (e) {
        // nothing done. Task has no valid duration. Will not be counted
      }
    })
    return info
  }, [jobReference, t_])
  const getExtendedColumns = useMemo(() => {
    if (!extendedColumns || extendedColumns.length === 0) {
      return null
    }
    return extendedColumns.map(extendedColumn => {
      if (extendedColumn.dataType === 'LIST') {
        return (
          <SmartSelect
            key={'jobReference-' + extendedColumn.label}
            label={extendedColumn.label}
            value={!jobReference || !jobReference[extendedColumn?.field] ? '' : jobReference[extendedColumn?.field]}
            onChange={(value) => {
              const newJobReference = structuredClone(jobReference)
              newJobReference[extendedColumn.field] = value
              updateJobReference(newJobReference)
            }}
            selectableOptions={extendedColumn.options}
            disabled={!editing}
            disableClearable
            minWidth='13rem'
          />
        )
      }
      return (
        <SmartTextField
          key={'jobReference-' + extendedColumn?.label}
          label={extendedColumn.label}
          value={!jobReference || !jobReference[extendedColumn?.field] ? '' : jobReference[extendedColumn?.field]}
          onChange={(newValue) => {
            const newJobReference = structuredClone(jobReference)
            newJobReference[extendedColumn.field] = newValue
            updateJobReference(newJobReference)
          }}
          disabled={!editing}
        />
      )
    })
  }, [editing, extendedColumns, jobReference, updateJobReference])

  // Story 80 — the selected mode is DERIVED from the stored timing instead of kept in component state.
  // With a second source of truth the panel could show a mode that is not what will be saved, and the two
  // would need resynchronising on every external re-render. S39 reads a saved value straight into a
  // disabled control, which only works if there is nothing to synchronise.
  const returnModes = useMemo(() => [
    { value: 'DATE', label: t_('Date') },
    { value: 'DURATION', label: t_('Duration') },
    { value: 'PERIODIC', label: t_('Periodic') }
  ], [t_])
  const returnModeOptions = returnModes.map((mode) => mode.label)
  const returnModeLabel =
    returnModes.find((mode) => mode.value === jobReference?.externalReturn?.mode)?.label ?? ''

  /**
   * Changing mode discards the other mode's value on purpose (RN10/S36): what gets persisted is always one
   * mode with its single value, so leaving the discarded one visible would show data that will not be
   * saved. Clearing the selection drops the whole object, which is how "not declared" travels.
   */
  const updateReturnMode = (label) => {
    const mode = returnModes.find((candidate) => candidate.label === label)?.value ?? null
    // Story 182 (S86): a PERIODIC timing carries its rule, empty until the user defines it. DATE and DURATION
    // keep the exact shape they had (no `periodicRule` key): the server discards it for them anyway (V10).
    const externalReturn = mode === 'PERIODIC'
      ? { mode, date: null, duration: null, periodicRule: null }
      : { mode, date: null, duration: null }
    updateJobReference({
      ...jobReference,
      externalReturn: mode === null ? null : externalReturn
    })
  }

  const updatePeriodicRule = (periodicRule) => {
    updateJobReference({ ...jobReference, externalReturn: { mode: 'PERIODIC', date: null, duration: null, periodicRule } })
  }

  const updateReturnDate = (date) => {
    updateJobReference({ ...jobReference, externalReturn: { mode: 'DATE', date, duration: null } })
  }

  const updateReturnDuration = (duration) => {
    updateJobReference({ ...jobReference, externalReturn: { mode: 'DURATION', date: null, duration } })
  }

  // S73/S74/S76/S116: guardar en biblioteca es de un nodo JOB de un workflow YA GUARDADO (hace
  // falta su id) que no venga ya de la biblioteca.
  const canSaveToLibrary = canPublishOperation && node?.nodeType === 'JOB' && !!workflowId
  // Historia 152 (S26/S27 · RN-G5, D10 + D12): el proceso de origen es un dato DEL NODO, no del
  // vínculo: lo tienen los JOB vinculados, los jobs locales copiados y los nodos WORKFLOW
  // arrastrados de una plantilla. Llega explícito por la cadena de props (punto frágil nº 1 de la
  // documentación) y, si no viaja, se lee del propio nodo. Sin él no se pinta NADA de origen: no se
  // inventa (CU-07 A1). El PAR se toma de una sola fuente —la prop o el nodo—, nunca mezclando el
  // código de una con la versión de la otra (`IN16`).
  const sourceProcess = sourceProcessCode
    ? { sourceProcessCode, sourceProcessVersionNumber }
    : { sourceProcessCode: node?.sourceProcessCode, sourceProcessVersionNumber: node?.sourceProcessVersionNumber }
  // Historia 152 (CA1.9, CA1.11 · RN-G15): las acciones del vínculo se ofrecen sobre un nodo con
  // vínculo VIVO —el estado que el panel pinta ahora mismo, personalización en pantalla incluida—
  // y con `OPERATION_LIBRARY`. Ver diferencias e ir a la ficha son SOLO LECTURA y no exigen modo
  // edición; desvincular, adoptar y proponer sí lo exigen: son cambios pendientes del editor
  // (RN-G12).
  const nodeOperationLink = operationLink || node?.operationLink || {}
  const linkState = resolveOperationLinkState(nodeOperationLink, linkedJobReference, jobReference)
  const hasLiveLink = canViewOperationLibrary && linkState.linked
  const showLinkHeader = !!(node?.operationLink || operationLink?.linked || operationLink?.state || operationLink?.intent || canSaveToLibrary)
  const hasOrigin = toSourceProcessPair(sourceProcess.sourceProcessCode, sourceProcess.sourceProcessVersionNumber).sourceProcessCode !== ''

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      {/* The link, the origin and their buttons sit in their own padded block: the rows below get their inner
          margin from the Grid items, and these three had none, glued to the edge of the panel (PO, 29/09/2026). */}
      {showLinkHeader || hasOrigin || hasLiveLink
        ? (
          <Stack spacing={1} data-testid='node-general-properties-heading' sx={{ px: 2, pt: 1.5 }}>
            {/* Historia 130 (S42/S55/S57/S85, D14): de dónde viene este JOB y, si su operación tiene una
                versión más nueva, el aviso para actualizar — mismo reparto que la cabecera del BOM.
                Historia 152 (S26): un vínculo con `state` cuenta aunque no esté vivo — un nodo
                DESVINCULADO tiene procedencia histórica que contar (`RN-01.18`). */}
            {showLinkHeader
              ? (
                <NodeOperationLinkHeader
                  operationLink={operationLink || node?.operationLink || {}}
                  linkedJobReference={linkedJobReference}
                  jobReference={jobReference}
                  workflowId={workflowId}
                  nodeId={node?.id}
                  onUpdate={(targetNodeId) => onOperationUpdate?.(targetNodeId)}
                  onDismiss={(versionNumber) => onOperationDismiss?.(versionNumber)}
                  editing={editing}
                  canPublish={canSaveToLibrary}
                  onPublishClick={() => setPublishDialogOpen(true)}
                  featureGroupRevisionControlled={featureGroupRevisionControlled}
                  featureGroupBaselines={featureGroupBaselines}
                />
                )
              : null}
            {/* Historia 152 (S26/S27, CU-07 · RN-01.13b punto 3, RN-G5): el ORIGEN del nodo. Una sola
                implementación (`NodeOriginBlock`), montada también por `Workflow.jsx` para los nodos
                WORKFLOW, que no pasan por este panel. */}
            <NodeOriginBlock {...sourceProcess} />
            {/* Historia 152 (S38, S47 · CA1.9, CA1.11): las acciones del vínculo vivo. Ninguna llama a un
                endpoint propio: no existen los de adoptar, proponer ni desvincular (§8.6). Lo que dejan
                es una INTENCIÓN pendiente que escribe el guardado general del workflow. */}
            {hasLiveLink
              ? (
                <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap data-testid='node-operation-link-actions'>
                  <Button size='small' variant='outlined' data-testid='node-view-differences' onClick={() => setCustomizationDialogOpen(true)}>
                    {t_('View differences')}
                  </Button>
                  {/* S29 (CA1.7 / RN-01.13c): ir a la FICHA de la operación en la vista OPERATIONS, con la
                      versión vinculada, para compararla con la de origen. Es navegación pura: no guarda
                      ni revierte, y el rótulo es el nombre de esa vista (J2), no una clave nueva. */}
                  <Button
                    size='small'
                    variant='outlined'
                    data-testid='node-open-operation'
                    onClick={() => onOpenOperationDetail?.({
                      nodeId: nodeOperationLink?.libraryNodeId || '',
                      code: linkState.code,
                      versionNumber: linkState.versionNumber
                    })}
                  >
                    {t_('Operations')}
                  </Button>
                  {editing
                    ? (
                      <Button size='small' variant='outlined' data-testid='node-unlink-operation' onClick={() => setUnlinkDialogOpen(true)}>
                        {t_('Unlink')}
                      </Button>
                      )
                    : null}
                </Stack>
                )
              : null}
          </Stack>
          )
        : null}
      {/* Historia 130 (ajuste del PO 2026-09-15): la identidad de la operación de biblioteca —código,
          nombre, descripción— y su tecnología. En un nodo del grafo nada de esto vive aquí: el código
          y el nombre son del NODO y la tecnología se cambia desde el lienzo. Una operación de
          biblioteca no tiene nodo en el lienzo, así que es aquí o en ningún sitio.
          El código solo se edita mientras la operación es nueva: en una ya guardada, cambiarlo sería
          otra operación distinta, no un renombrado. */}
      {technologyEditable
        ? (
          <Grid
            container
            spacing={2}
            alignItems='center'
            data-testid='operation-identity'
            sx={{ paddingTop: 1, paddingRight: 2 }}
          >
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                size='small'
                label={t_('Operation code')}
                value={operationIdentity.code || ''}
                disabled={!editing || !operationIdentity.isNew}
                inputProps={{ maxLength: 100 }}
                onChange={(event) => onOperationIdentityChange?.({ code: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size='small'
                label={t_('Operation name')}
                value={operationIdentity.name || ''}
                disabled={!editing}
                inputProps={{ maxLength: 255 }}
                onChange={(event) => onOperationIdentityChange?.({ name: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size='small'
                label={t_('Description')}
                value={operationIdentity.description || ''}
                disabled={!editing}
                inputProps={{ maxLength: 255 }}
                onChange={(event) => onOperationIdentityChange?.({ description: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SmartSelect
                label={t_('Technology')}
                data-testid='operation-technology-select'
                value={jobReference?.technology?.name || ''} // system-value: raw-ok
                onChange={changeTechnology}
                selectableOptions={technologies.map((option) => option.name)}
                // Story 204 — S75: each technology reads by its code; the value chosen is still its name.
                optionLabelTransform={(name) => systemValueOptionText(t_, 'technology', technologies, name)}
                disabled={!editing}
                disableClearable
                size='small'
                minWidth='100%'
              />
            </Grid>
          </Grid>
          )
        : null}
      {/* Decisión del PO (2026-09-15): publicar una operación DESDE un nodo del workflow (HU-04) sale
          del alcance de esta entrega — el circuito de Sun no está cerrado y ofrecerlo confunde. El
          diálogo `PublishOperationDialog` se queda construido y probado, pero sin punto de entrada. */}
      <Grid
        container
        spacing={2}
        alignItems='center'
        wrap='nowrap'
        sx={{ overflowX: 'auto' }}
      >
        <Grid item xs='auto' sx={{ mr: 1 }}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography color='primary' noWrap>{t_('Tools') + ':'}</Typography>
            <Typography sx={{ fontSize: '0.975rem' }}>{jobInfo.toolQuantity}</Typography>
          </Stack>
        </Grid>
        <Grid item xs='auto' sx={{ mr: 1 }}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography color='primary' noWrap>{t_('Total time') + ':'}</Typography>
            <Typography sx={{ fontSize: '0.975rem' }}>{GetUtcTime(jobInfo.totalTime)}</Typography>
          </Stack>
        </Grid>

        <Grid item xs='auto' sx={{ mr: 1 }}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography color='primary' noWrap>{t_('Step name') + ':'}</Typography>
            <Typography sx={{ fontSize: '0.975rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
              {jobReference?.name || systemValueLabel(t_, 'technology', jobReference?.technology)}
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs='auto' wrap='nowrap' sx={{ mr: 1 }}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography color='primary' noWrap>{t_('Operators') + ':'}</Typography>
            <TextField
              type='number'
              value={jobReference?.operatorQuantity || 0}
              inputProps={{ min: 0, 'data-testid': 'job-operator-quantity' }}
              disabled={jobOperatorsDisabled}
              sx={{ width: 60 }}
              onChange={(event) => {
                const operatorQuantity = event.target.value
                updateJobReference({ ...jobReference, operatorQuantity })
              }}
              InputLabelProps={{ shrink: true }}
              size='small'
            />
          </Stack>
        </Grid>

        <Grid item xs='auto' sx={{ mr: 1 }}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography noWrap color='primary'>{t_('Minimum quantity') + ':'}</Typography>
            <TextField
              type='number'
              value={jobReference?.minimumQuantity || 1}
              inputProps={{ min: 1 }}
              disabled={!editing}
              size='small'
              sx={{ width: 60 }}
              onChange={(event) => {
                const minimumQuantity = event.target.value
                updateJobReference({ ...jobReference, minimumQuantity })
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Grid>
        <Grid item xs='auto'>
          <Stack direction='row' spacing={2} alignItems='center'>
            <Typography color='primary'>{t_('Extern') + ':'}</Typography>
            {/* S52/RN24: every block explains its own reason. The tooltip wraps a plain span because a
                disabled MUI control receives no pointer events, and with the default `describeChild`
                MUI exposes the reason as a native `aria-label` on that span. RN23: blocked is neither
                hidden nor emptied — the checkbox always shows the persisted mark. */}
            <Tooltip title={externGating.actionable ? '' : t_(externGating.blockReason)}>
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={jobReference?.external === true}
                      disabled={!externGating.actionable}
                      disableRipple
                      inputProps={{ 'aria-label': t_('Extern') }}
                      onChange={(event) => changeExternMark(event.target.checked)}
                    />
                  }
                />
              </span>
            </Tooltip>
            {/* Story 113 (S98): ONE checkbox covers RN3 at BOTH levels, because this panel serves the
                reference workflow and the fabrication order's alike. RN23: blocked is neither hidden
                nor emptied -- the persisted mark stays readable either way. */}
            <Typography color='primary'>{t_('Manage preparation') + ':'}</Typography>
            <Tooltip title={preparationGating.actionable ? '' : t_(preparationGating.blockReason)}>
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={managesPreparation(jobReference)}
                      disabled={!preparationGating.actionable}
                      disableRipple
                      inputProps={{ 'aria-label': t_('Manage preparation') }}
                      onChange={(event) =>
                        updateJobReference({ ...jobReference, managePreparation: event.target.checked })}
                    />
                  }
                />
              </span>
            </Tooltip>
            {/* Story 183 (S8): the night mark, with the same Tooltip + span + FormControlLabel shape as
                its siblings. RN6.1: blocked is neither hidden nor emptied — it always shows the mark. */}
            {nightJobContext.enabled
              ? (
                <>
                  <Typography color='primary'>{t_('Night job') + ':'}</Typography>
                  <Tooltip title={nightGating.actionable ? '' : t_(nightGating.blockReason)}>
                    <span>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isNightJobEffective(jobReference)}
                            disabled={!nightGating.actionable}
                            disableRipple
                            inputProps={{ 'aria-label': t_('Night job') }}
                            onChange={(event) => updateJobReference({ ...jobReference, nightJob: event.target.checked })}
                          />
                        }
                      />
                    </span>
                  </Tooltip>
                </>
                )
              : null}
            {workflowUseIndex
              ? <SmartSelect
                  value={jobReference?.indexValue?.name ?? ''}
                  // S60/S62 (HU-04, enmienda PO 3): same behavior as the workflow-level selector —
                  // hide the indexValue options the jobReference cannot reach given its
                  // featureGroups (a DRAFT featureGroup leaves only DRAFT; a PROPOSAL one excludes
                  // APPROVED), mirroring the rule Sun enforces on save. Enmienda PO 23/07/2026:
                  // with WORKFLOW_CASCADE_APPROVAL active the options are NOT filtered — instead a
                  // cascade confirmation modal is offered on change.
                  selectableOptions={(cascadeApprovalEnabled ? indexValues : filterAdmissibleIndexValues(indexValues, collectFeatureGroupIndexValues(jobReference))).map(index => index.name)}
                  size='small'
                  label={t_('Index')}
                  disabled={!editing}
                  onChange={(newValue) => {
                    const indexValue = indexValues.find(index => index.name === newValue)
                    if (cascadeApprovalEnabled) {
                      // Story 186 (RN49): the cascade never blocks; the dialog warns about the feature groups still in DRAFT.
                      const items = collectJobReferenceCascadeItems(jobReference, indexValue.name, node, featureGroupCascadePolicy)
                      if (items.length > 0) {
                        setCascadeDialog({ open: true, targetIndexValue: indexValue, items })
                        return
                      }
                    }
                    updateJobReference({ ...jobReference, indexValue })
                  }}
                  minWidth='9rem'
                />
              : null}
          </Stack>
        </Grid>

        {/*
          Story 80 — when the subcontracting of an external job returns. Rendered only for an external
          job: with EXT off the block does not exist in the DOM at all, so a job that is not external is
          exactly what it was before this story (S31).
          One mode per control (S33/S34). There is deliberately no calendar checkbox: a working calendar
          hangs off a device and an external job has none, so the duration is natural time by definition.

          Two layout rules this block learned the hard way, both of which cost a real overlap bug:
          - NO `flexWrap` on the Stack. A row Stack's `spacing` is margin-left only, so a wrapped second
            line gets zero vertical gap and the floating label of the control below is drawn over the
            control above. The row already scrolls horizontally (`overflowX: 'auto'` on the container),
            which is how every sibling block of this row handles not fitting.
          - Every control bounds its own width, like its siblings do. `SmartSelect` needs an explicit
            `width` or it wraps itself in a `width: 100%` div and eats the whole line.
        */}
        {isExternalEffective(jobReference) &&
          <Grid item xs='auto' sx={{ mr: 1 }}>
            <Stack direction='row' spacing={2} alignItems='center'>
              <Typography color='primary' noWrap>{t_('Return timing') + ':'}</Typography>
              <SmartSelect
                value={returnModeLabel}
                selectableOptions={returnModeOptions}
                size='small'
                label={t_('Return mode')}
                disabled={!editing}
                onChange={(newValue) => updateReturnMode(newValue)}
                minWidth='9rem'
                width='9rem'
              />
              {returnModeLabel === t_('Date')
                ? <ReturnDateField
                    value={jobReference?.externalReturn?.date}
                    disabled={!editing}
                    onChange={(newValue) => updateReturnDate(newValue)}
                  />
                : null}
              {returnModeLabel === t_('Duration')
                ? <ReturnDurationField
                    value={jobReference?.externalReturn?.duration ?? ''}
                    disabled={!editing}
                    onChange={(newValue) => updateReturnDuration(newValue)}
                  />
                : null}
              {/* Story 182 — the rule editor does not fit in this non-wrapping row: a field with the readable
                  rule opens it in a popover (PeriodicReturnPopoverField). S124 (RN43.2): keyed by node, because
                  Workflow.jsx keeps this panel mounted while the selected node changes and the per-form drafts
                  of one node must never be emitted in another. */}
              {returnModeLabel === t_('Periodic')
                ? <PeriodicReturnPopoverField
                    key={node?.id ?? jobReference?.id}
                    rule={jobReference?.externalReturn?.periodicRule ?? null}
                    disabled={!editing}
                    onChange={updatePeriodicRule}
                  />
                : null}
            </Stack>
          </Grid>}

      </Grid>

      <Grid container spacing={2} alignItems='center'>

        <Grid item xs={12} sm={4}>
          <TextField
            label={t_('Remark') + ':'}
            value={jobReference?.remark || ''}
            disabled={!editing}
            size='small'
            fullWidth
            onChange={(event) => {
              const remark = event.target.value
              updateJobReference({ ...jobReference, remark })
            }}
            multiline
            inputProps={{ maxLength: 999 }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          {/* S48/S49 (D3/RN1): DEDICATED field for the planning group — NOT one of the 12 extended
              columns rendered dynamically below, which keep coming from
              `apiGet('extendedColumn/job_reference')` untouched. Same edit gating as the rest of
              the job's properties (`disabled={!editing}`), no permission or gating of its own. */}
          <SmartTextField
            label={t_('Planning group')}
            value={jobReference?.planningGroup || ''}
            disabled={!editing}
            size='small'
            onChange={(newValue) => {
              updateJobReference({ ...jobReference, planningGroup: newValue })
            }}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <SmartSelect
            value={jobReference?.progressCalculationTypeDTO?.name ?? ''}
            selectableOptions={progressCalculationTypes.map(p => p.name)}
            optionLabelTransform={(name) => systemValueOptionText(t_, 'progress_calculation_type', progressCalculationTypes, name)}
            size='small'
            width='100%'
            label={t_('Progress calculation')}
            disabled={!editing}
            onChange={(newValue) => {
              const pct = progressCalculationTypes.find(p => p.name === newValue)
              updateJobReference({ ...jobReference, progressCalculationTypeDTO: pct })
            }}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <SmartSelect
            value={preferenceDeviceBlocked ? '' : jobReference?.preferenceDevice?.name}
            selectableOptions={deviceList.map((device) => { return device.name })}
            size='small'
            width='100%'
            label={t_('Preference Asset')}
            optionWordBreak='break-all'
            autoSelectFirst={false}
            disabled={!editing || preferenceDeviceBlocked}
            onChange={(newValue) => {
              const preferenceDevice = deviceList.find(device => device.name === newValue)
              updateJobReference({ ...jobReference, preferenceDevice })
            }}
            toolTip
            disableClearable={false}
          />
          {preferenceDeviceBlocked
            ? (
              <Typography variant='caption' color='primary'>
                {t_('An external job is planned on an external asset')}
              </Typography>
              )
            : null}
        </Grid>
        <Grid item xs={12} sm={3}>
          <SmartSelect
            value={jobReference?.rule?.name}
            selectableOptions={ruleList.map(rule => rule.name)}
            multiple={false}
            size='small'
            width='90%'
            label={t_('Rule')}
            autoSelectFirst={false}
            disabled={!editing}
            disableClearable={false}
            onChange={(newValue) => {
              const rule = ruleList.find(rule => rule.name === newValue)
              updateJobReference({ ...jobReference, rule })
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <SmartSelect
            value={jobReference?.fixtureReference?.name}
            selectableOptions={fixtureRefList.map(fixture => fixture.name)}
            multiple={false}
            size='small'
            width='90%'
            label={t_('Fixture reference')}
            autoSelectFirst={false}
            disabled={!editing}
            disableClearable={false}
            onChange={(newValue) => {
              const fixtureReference = fixtureRefList.find(fixture => fixture.name === newValue)
              updateJobReference({ ...jobReference, fixtureReference })
            }}
          />
        </Grid>
      </Grid>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '1rem', marginLeft: '1rem', marginTop: '1rem' }}>
        {getExtendedColumns}
      </div>
      <CascadeIndexChangeDialog
        open={cascadeDialog.open}
        subjectLabel={`${t_('Job')} ${formatCodeName(node?.code, node?.name)}`}
        previousIndexValueName={jobReference?.indexValue?.name}
        targetIndexValueName={cascadeDialog.targetIndexValue.name}
        items={cascadeDialog.items}
        onAccept={() => {
          updateJobReference(applyJobReferenceCascadeIndexValue(jobReference, cascadeDialog.targetIndexValue, featureGroupCascadePolicy))
          setCascadeDialog({ open: false, targetIndexValue: {}, items: [] })
        }}
        onCancel={() => {
          setCascadeDialog({ open: false, targetIndexValue: {}, items: [] })
        }}
      />
      <ExternResetDialog
        open={externResetDialogOpen}
        onAccept={() => {
          updateJobReference(markExternalEmptyingPlannerFields(jobReference))
          setExternResetDialogOpen(false)
        }}
        onCancel={() => setExternResetDialogOpen(false)}
      />
      {/* S73/S74/S116 (CA4.1 / RN-04.1, RN-07.1): el diálogo de guardar en la biblioteca. Publicar no
          escribe nada por su cuenta (Δ9): deja la intención en el nodo y la escribe el guardado
          general del workflow. */}
      {canSaveToLibrary && !operationLink?.linked && !node?.operationLink?.linked
        ? (
          <PublishOperationDialog
            open={publishDialogOpen}
            workflowId={workflowId}
            nodeId={node.id}
            onPublish={(publish) => {
              setPublishDialogOpen(false)
              onOperationPublish?.(publish)
            }}
            onClose={() => setPublishDialogOpen(false)}
          />
          )
        : null}
      {/* Historia 152 (S38/S40/S44 · CA1.9): E4 lleva dentro la comparación de P2 y las dos acciones
          que salen de ella. `canPropose` es el permiso de ESCRITURA de la biblioteca; que el nodo
          esté personalizado lo decide el propio diálogo con lo que diga P2 o con el estado que este
          panel ya está pintando. */}
      {hasLiveLink && customizationDialogOpen
        ? (
          <OperationCustomizationDialog
            open
            workflowId={workflowId}
            nodeId={node?.id}
            operationLink={{ ...nodeOperationLink, state: linkState.state }}
            editing={editing}
            canPropose={canPublishOperation}
            featureGroupRevisionControlled={featureGroupRevisionControlled}
            jobReference={jobReference}
            featureGroupBaselines={featureGroupBaselines}
            onAdopt={(targetNodeId) => {
              setCustomizationDialogOpen(false)
              onOperationUpdate?.(targetNodeId)
            }}
            onPropose={(publish) => {
              setCustomizationDialogOpen(false)
              onOperationPublish?.(publish)
            }}
            onClose={() => setCustomizationDialogOpen(false)}
          />
          )
        : null}
      {/* S47: desvincular pasa SIEMPRE por la confirmación; el handler solo se llama al aceptar. */}
      {hasLiveLink && unlinkDialogOpen
        ? (
          <UnlinkOperationDialog
            open
            code={linkState.code}
            versionNumber={linkState.versionNumber}
            onConfirm={() => {
              setUnlinkDialogOpen(false)
              onOperationUnlink?.()
            }}
            onClose={() => setUnlinkDialogOpen(false)}
          />
          )
        : null}
    </Stack>
  )
}

export default NodeGeneralProperties
